import { CalendarClock } from "lucide-react";
import type { ReactNode } from "react";
import { SimpleTooltip } from "@/components/ui/tooltip";
import { type PersonPendingChange, usePersonPendingChanges } from "@/hooks/use-persons";
import { formatEffectiveDate } from "@/lib/dates";

/**
 * Notification icon shown beside a field with one or more scheduled changes.
 * Hovering reads "Effective on MM/DD/YYYY this record will change to X".
 */
function PendingChangeIcon({ changes }: { changes: PersonPendingChange[] }) {
  if (changes.length === 0) return null;
  return (
    <SimpleTooltip
      content={
        <div className="space-y-0.5">
          {changes.map((c) => (
            <div key={`${c.field}-${c.effectiveDate}`}>
              Effective on {formatEffectiveDate(c.effectiveDate)} this record will change to{" "}
              <span className="font-medium">{c.newValueLabel}</span>
            </div>
          ))}
        </div>
      }
    >
      <span
        // Pinned to the dark-mode brand blue in both themes: `text-primary`
        // resolves to navy in light mode, but we want the light-blue change
        // marker consistently regardless of theme.
        className="inline-flex cursor-help text-[#4f93d8]"
        role="img"
        aria-label="Has a scheduled change"
      >
        <CalendarClock className="h-3.5 w-3.5" />
      </span>
    </SimpleTooltip>
  );
}

/** Marks a metadata row when any of the given fields has a change queued. */
export type Adorn = (...fields: string[]) => ReactNode;

/**
 * The scheduled-change marker, shared by the three cards on the Overview tab.
 *
 * A field can carry several stacked changes across different effective dates,
 * so each field is indexed to the full list rather than a single change —
 * otherwise the hover tooltip would only ever show the last one.
 *
 * All three cards read the same query, so calling this in each of them costs
 * one request, not three.
 */
export function usePendingAdornments(personId: string): Adorn {
  const { data: pending = [] } = usePersonPendingChanges(personId);

  const byField = new Map<string, PersonPendingChange[]>();
  for (const p of pending) {
    const list = byField.get(p.field);
    if (list) list.push(p);
    else byField.set(p.field, [p]);
  }

  return (...fields: string[]) => {
    const found = fields.flatMap((f) => byField.get(f) ?? []);
    return found.length > 0 ? <PendingChangeIcon changes={found} /> : undefined;
  };
}
